import { useCallback, useEffect, useState } from 'react';
import CallRow from './CallRow';
import VoicemailCard from './VoicemailCard';
import salesService from '../../services/sales';
import { errorMessage } from '../../utils/apiResponse';
import { useOrgTimezone } from '../../utils/timezone';
import type { Call, SalesPerson, Voicemail } from '../../types/sales';

/**
 * A customer's Calls tab (Sales seat): the voicemails they left and every
 * call with them, newest first, with recordings and transcripts for those
 * who may hear them. Shown on /accounts/customers/:id?tab=calls.
 */
export default function CustomerCallsTab({ customerId }: { customerId: number }) {
  const tz = useOrgTimezone();
  const [calls, setCalls] = useState<Call[] | null>(null);
  const [voicemails, setVoicemails] = useState<Voicemail[] | null>(null);
  const [people, setPeople] = useState<SalesPerson[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, v] = await Promise.all([
        salesService.listCalls({ customerId, limit: 200 }),
        salesService.listVoicemails('all', customerId),
      ]);
      if (!c.success) throw c;
      if (!v.success) throw v;
      setCalls(c.data);
      setVoicemails(v.data);
    } catch (err) {
      setError(errorMessage(err, 'Could not load the calls'));
    }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    salesService.listSalesPeople().then((r) => { if (r.success) setPeople(r.data); }).catch(() => {});
  }, []);

  if (error) return <p className="text-sm text-red-700 break-words">{error}</p>;
  if (!calls || !voicemails) return <div className="h-32 bg-neutral-100 rounded-xl animate-pulse" />;

  return (
    <div className="space-y-4">
      {voicemails.length > 0 && (
        <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
          <h3 className="p-4 border-b border-neutral-100 text-base font-semibold text-neutral-900">Voicemails</h3>
          <ul className="divide-y divide-neutral-100">
            {voicemails.map((v) => (
              <VoicemailCard key={v.id} vm={v} people={people} tz={tz} linkCustomer={false}
                onChange={(next) => setVoicemails((list) => list && list.map((x) => (x.id === next.id ? next : x)))} />
            ))}
          </ul>
        </section>
      )}
      <section className="bg-white border border-neutral-200 rounded-xl min-w-0">
        <h3 className="p-4 border-b border-neutral-100 text-base font-semibold text-neutral-900">Calls</h3>
        {calls.length === 0 ? (
          <p className="p-4 text-sm text-neutral-600">No calls with this customer yet. A call is put against them when its number is one of theirs.</p>
        ) : (
          <ul className="divide-y divide-neutral-100">
            {calls.map((c) => <CallRow key={c.id} call={c} tz={tz} linkCustomer={false} />)}
          </ul>
        )}
      </section>
    </div>
  );
}
